import React, {
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import getClientConfig from 'services/UrqlClient/UrqlClient';
import useWsClient from 'services/useWsClient';
import { AuthContext, AuthContextProps } from 'shared/providers/auth';
import {
  EnvironmentContext,
  EnvironmentContextProps,
} from 'shared/providers/environment';
import { onErrorModeChange } from 'shared/utils/offlineSimulator';
import { ClientOptions, createClient, Provider } from 'urql';

const UrqlProvider = ({ children }: PropsWithChildren) => {
  // Recreating the client resets cache, exchanges, and subscriptions.
  // So here we tell React compiler not to run useMemo optimizations on this provider.
  'use no memo';

  const { isLoggedIn, handleLogout, idToken, varoSessionId } =
    useContext<AuthContextProps>(AuthContext);
  const { currentEnv, getGQLUrl } =
    useContext<EnvironmentContextProps>(EnvironmentContext);

  const gqlUrl = getGQLUrl(currentEnv);
  const wsClient = useWsClient();

  // Dev-only: invalidate the client when the error simulator toggles
  const [devResetKey, setDevResetKey] = useState(0);
  useEffect(() => {
    if (!__DEV__) {
      return;
    }
    return onErrorModeChange(() => setDevResetKey((k) => k + 1));
  }, []);

  const client = useMemo(() => {
    // this re-initializes authExchange with the new idToken
    return createClient(
      getClientConfig(
        gqlUrl,
        handleLogout,
        wsClient,
        idToken,
        varoSessionId,
      ) as ClientOptions,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn, gqlUrl, idToken, varoSessionId, wsClient, devResetKey]);

  return <Provider value={client}>{children}</Provider>;
};

export default UrqlProvider;
